// Drawn birds for Птицелов: his magpies (summonView.ts) and his falcon in flight (fx.ts). Built from a few low-poly
// ellipsoids and thin extruded plates through engrave.mk(), so they hatch like units and stay light for a flock of
// three per hero (a few hundred triangles each). Both face +z with +y up; a wing is a pivot group whose rotation.z
// flaps it (positive lifts the left wing, the caller mirrors by `userData.side`).
import * as THREE from 'three';
import { mk } from './engrave.js';

const MAGPIE = 0x26262a, MAGPIE_WHITE = 0xe8e4d8, BEAK = 0x1a1a1a;
const FALCON = 0x6a5238, FALCON_DARK = 0x46382a, FALCON_PALE = 0xc8b898;

/** A flat plate through (x, z) points in the horizontal plane, `thick` tall, centred on y = 0. */
function plate(pts: [number, number][], thick: number) {
  const v = pts.map(([x, z]) => new THREE.Vector2(x, z));
  if (THREE.ShapeUtils.isClockWise(v)) v.reverse();
  const g = new THREE.ExtrudeGeometry(new THREE.Shape(v), { depth: thick, bevelEnabled: false });
  g.rotateX(Math.PI / 2);  // shape y → z, extrusion → -y
  g.translate(0, thick / 2, 0);
  return g;
}
const mirror = (pts: [number, number][], side: number) => pts.map(([x, z]) => [x * side, z] as [number, number]);

function ellipsoid(parent: THREE.Object3D, r: number, color: number, pos: [number, number, number], scale: [number, number, number], opts = {}) {
  const m = mk(new THREE.SphereGeometry(r, 8, 6), color, { team: 'unit', ...opts });
  m.position.set(...pos);
  m.scale.set(...scale);
  parent.add(m);
  return m;
}

function wing(parent: THREE.Object3D, side: number, at: [number, number, number], outline: [number, number][], color: number,
              patch: { pts: [number, number][]; color: number } | null, opts = {}) {
  const pivot = new THREE.Group();
  pivot.position.set(...at);
  pivot.userData.side = side;
  pivot.add(mk(plate(mirror(outline, side), 0.03), color, { team: 'unit', ...opts }));
  if (patch) {
    const p = mk(plate(mirror(patch.pts, side), 0.02), patch.color, { team: 'unit', ...opts });
    p.position.y = 0.02;
    pivot.add(p);
  }
  parent.add(pivot);
  return pivot;
}

/** A magpie: black with a white belly and white shoulder patches, a long graduated tail. Returns its two wing pivots. */
export function buildMagpie(body: THREE.Group): THREE.Group[] {
  ellipsoid(body, 0.2, MAGPIE, [0, 0, 0], [0.85, 0.8, 1.55]);
  ellipsoid(body, 0.15, MAGPIE_WHITE, [0, -0.07, 0.04], [0.9, 0.65, 1.3]);
  ellipsoid(body, 0.12, MAGPIE, [0, 0.1, 0.33], [1, 1, 1.1]);
  const beak = mk(new THREE.ConeGeometry(0.035, 0.17, 4), BEAK, { team: 'unit' });
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, 0.08, 0.5);
  body.add(beak);
  const tail = mk(plate([[0.05, -0.2], [-0.05, -0.2], [-0.1, -0.88], [0, -0.95], [0.1, -0.88]], 0.03), MAGPIE, { team: 'unit' });
  tail.rotation.x = -0.18;
  tail.position.y = 0.03;
  body.add(tail);
  const OUTLINE: [number, number][] = [[0, 0.16], [0.3, 0.14], [0.64, -0.04], [0.58, -0.16], [0.28, -0.17], [0, -0.14]];
  const PATCH: [number, number][] = [[0.04, 0.1], [0.26, 0.09], [0.3, -0.06], [0.05, -0.09]];
  return [-1, 1].map(side => wing(body, side, [side * 0.13, 0.06, 0.02], OUTLINE, MAGPIE, { pts: PATCH, color: MAGPIE_WHITE }));
}

/** The falcon stooping: a tapered brown body, pale breast, hooked beak, long pointed wings swept back. */
export function buildFalcon(): THREE.Group {
  const g = new THREE.Group(), opts = { cast: false };
  ellipsoid(g, 0.17, FALCON, [0, 0, 0], [0.75, 0.65, 1.7], opts);
  ellipsoid(g, 0.13, FALCON_PALE, [0, -0.05, 0.06], [0.8, 0.6, 1.4], opts);
  ellipsoid(g, 0.1, FALCON_DARK, [0, 0.04, 0.3], [0.9, 0.9, 1.1], opts);
  const beak = mk(new THREE.ConeGeometry(0.03, 0.1, 4), 0x2e2a26, { team: 'unit', cast: false });
  beak.rotation.x = Math.PI / 2 + 0.5;  // hooked down
  beak.position.set(0, 0.0, 0.42);
  g.add(beak);
  g.add(mk(plate([[0.05, -0.22], [-0.05, -0.22], [-0.1, -0.52], [0.1, -0.52]], 0.025), FALCON_DARK, { team: 'unit', cast: false }));
  const OUTLINE: [number, number][] = [[0, 0.14], [0.28, 0.06], [0.62, -0.34], [0.46, -0.28], [0.2, -0.14], [0, -0.1]];
  for (const side of [-1, 1]) wing(g, side, [side * 0.08, 0.04, 0.02], OUTLINE, FALCON, null, opts).rotation.z = side * 0.15;
  return g;
}
